import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Star, User } from 'lucide-react';
import { calculateDistance } from '@/utils/distanceCalculation';
import { supabase } from '@/integrations/supabase/client';

interface RideRequestData {
  id: string;
  pickup_address: string;
  dropoff_address: string;
  pickup_latitude: number;
  pickup_longitude: number;
  dropoff_latitude: number;
  dropoff_longitude: number;
  estimated_fare: number;
  distance_km: number;
  ride_type: string;
  customer_id: string;
  customer_name?: string;
  customer_rating?: number;
}

interface RideRequestPopupProps {
  request: RideRequestData;
  driverLocation?: { lat: number; lng: number } | null;
  onAccept: (requestId: string) => void;
  onDecline: (requestId: string) => void;
}

const RideRequestPopup = ({ request, driverLocation, onAccept, onDecline }: RideRequestPopupProps) => {
  const [timeLeft, setTimeLeft] = useState(30);
  const [customerName, setCustomerName] = useState(request.customer_name || '');
  const [customerRating, setCustomerRating] = useState(request.customer_rating || 0);

  // Fetch customer name from profiles
  useEffect(() => {
    if (customerName) return;
    const fetchCustomer = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', request.customer_id)
        .maybeSingle();
      if (data?.full_name) setCustomerName(data.full_name);
    };
    fetchCustomer();
  }, [request.customer_id, customerName]);

  // Auto-decline after timeout
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          onDecline(request.id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [request.id, onDecline]);

  // Distance from driver to pickup
  const distanceToPickup = driverLocation
    ? calculateDistance(driverLocation.lat, driverLocation.lng, request.pickup_latitude, request.pickup_longitude)
    : null;
  const etaMinutes = distanceToPickup ? Math.round(distanceToPickup * 3) : null;

  // Trip duration estimate (3 min/km)
  const tripMinutes = Math.round(request.distance_km * 3);

  return (
    <div className="fixed inset-0 z-[3000] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => onDecline(request.id)} />
      <div className="relative w-full max-w-md bg-white rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300">
        {/* Timer bar */}
        <div className="h-1 bg-gray-200 rounded-t-3xl overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-1000"
            style={{ width: `${(timeLeft / 30) * 100}%` }}
          />
        </div>

        <div className="p-6">
          {/* Customer name + rating */}
          <div className="flex flex-col items-center mb-4">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-2">
              <User className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-xl font-bold">{customerName || 'Customer'}</p>
            {customerRating > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-medium">{customerRating.toFixed(1)}</span>
              </div>
            )}
            <p className="text-3xl font-bold mt-2">D{request.estimated_fare.toFixed(0)}</p>
          </div>

          {/* Pickup info */}
          <div className="space-y-3 mb-5">
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center mt-1">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <div className="w-0.5 h-8 bg-gray-300 my-1" />
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {etaMinutes ? `${etaMinutes} min` : ''} {distanceToPickup ? `(${distanceToPickup.toFixed(1)} km) away` : ''}
                  </p>
                  <p className="font-medium text-sm truncate">{request.pickup_address}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{tripMinutes} min ({request.distance_km.toFixed(1)} km) trip</p>
                  <p className="font-medium text-sm truncate">{request.dropoff_address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onDecline(request.id)}
              className="flex-1 h-14 text-lg font-semibold rounded-xl border-2"
            >
              Decline
            </Button>
            <Button
              onClick={() => onAccept(request.id)}
              className="flex-1 h-14 text-lg font-semibold rounded-xl bg-green-600 hover:bg-green-700 text-white"
            >
              Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideRequestPopup;
